import clsx from "clsx";
import { motion } from "framer-motion";
import Badge from "../Badge/Badge";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { ICON_SIZES } from "src/helpers/constants";
import { MdHub } from "react-icons/md";

interface ILinksList {
  links: Array<any>;
  rightSide?: ReactNode;
  id?: string;
}

const LinksList: React.FC<ILinksList> = ({ links, rightSide = <></>, id }) => {
  const linksList = links?.filter?.(({ hide }) => !hide);
  const containerRef = useRef<HTMLUListElement | null>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [animatedItems, setAnimatedItems] = useState(0);
  const hasScrolledInitially = useRef(false);
  const hasMounted = useRef(false);
  const [visuallyActiveIndices, setVisuallyActiveIndices] = useState<number[]>(
    []
  );

  const scrollToItem = (index: number, behavior: ScrollBehavior = "smooth") => {
    const item = itemRefs.current[index];
    const container = containerRef.current;

    if (item && container) {
      const isFirst = index === 0;
      const scrollLeft = isFirst
        ? 0
        : item.offsetLeft - container.offsetWidth / 2 + item.offsetWidth / 2;

      container.scrollTo({ left: scrollLeft, behavior });
    }
  };

  useEffect(() => {
    if (
      !hasScrolledInitially.current &&
      animatedItems === linksList.length &&
      linksList.length > 0
    ) {
      const activeIndex = linksList.findIndex((item) => item.active);
      if (activeIndex !== -1) {
        scrollToItem(activeIndex, "auto");
        requestAnimationFrame(() => {
          setVisuallyActiveIndices((prev) => [...prev, activeIndex]);
        });
      }
      hasScrolledInitially.current = true;
      hasMounted.current = true;
    }
  }, [animatedItems, linksList]);

  return (
    <div
      className={clsx([
        "flex",
        "w-full",
        "justify-between",
        "items-center",
        "rounded-lg",
        "overflow-hidden",
        "px-0",
        "md:py-1",
        "py-2",
        "gap-6",
      ])}
      id={id}
    >
      <ul
        ref={containerRef}
        className={clsx([
          "gap-6",
          "md:gap-12",
          "flex",
          "md:w-full",
          "overflow-x-auto",
          "no-scrollbar",
          "w-full",
          "box-content",
          "min-h-[50px]",
          "scroll-smooth",
        ])}
      >
        {linksList?.map?.(
          (
            {
              text,
              onClick,
              active,
              icon,
              isLoading,
              count,
              showCount = false,
            },
            key: number
          ) => {
            const isVisuallyActive = visuallyActiveIndices.includes(key);

            useEffect(() => {
              if (active && hasMounted.current && !isVisuallyActive) {
                setVisuallyActiveIndices((prev) => [...prev, key]);
              }
              if (!active && isVisuallyActive) {
                setVisuallyActiveIndices((prev) =>
                  prev.filter((i) => i !== key)
                );
              }
            }, [active]);

            return (
              <motion.li
                ref={(el) => (itemRefs.current[key] = el)}
                className={clsx([
                  "transition-colors",
                  "inline-flex",
                  "flex-row",
                  "md:flex",
                  "items-center",
                  "gap-1",
                  "md:gap-1",
                  "pb-1",
                  "text-sm",
                  "md:text-base",
                  "cursor-pointer",
                  "relative",
                  "group",
                  "whitespace-nowrap",
                  "md:py-2",
                  "last:border-l-0",
                  !isVisuallyActive && "text-gray-600 dark:text-gray-200",
                  isVisuallyActive && "text-red-500 dark:text-cyan-400",
                  !isVisuallyActive &&
                    "md:hover:text-red-500 md:dark:hover:text-cyan-300",
                ])}
                onClick={() => {
                  onClick?.();
                  scrollToItem(key, "smooth");
                  setVisuallyActiveIndices((prev) => [
                    ...new Set([...prev, key]),
                  ]);
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.2,
                  ease: "easeOut",
                  delay: key * 0.1,
                  type: "spring",
                  stiffness: 400,
                  damping: 17,
                }}
                onAnimationComplete={() => {
                  setAnimatedItems((prev) => prev + 1);
                }}
                key={key}
              >
                {isLoading ? (
                  <div
                    className={clsx([
                      `h-[${ICON_SIZES.MD_LG}px]`,
                      `w-[${ICON_SIZES.MD_LG}px]`,
                      "flex",
                      "items-center",
                      "justify-center",
                    ])}
                  >
                    <MdHub size={ICON_SIZES.MD} className="animate-spin" />
                  </div>
                ) : (
                  <span
                    className={clsx([
                      !isVisuallyActive && "group-hover:animate-pulse",
                    ])}
                  >
                    {icon}
                  </span>
                )}
                <span className={clsx(["font-medium", "mds:uppercase"])}>
                  {text}
                </span>
                {showCount && (
                  <Badge
                    text={count}
                    className={clsx([
                      "min-w-[50px]",
                      "text-center",
                      "justify-center",
                    ])}
                  />
                )}
                <motion.div
                  className={clsx([
                    "absolute",
                    "bottom-0",
                    "h-[3px]",
                    "rounded-full",
                    "bg-red-400/70",
                    "dark:bg-cyan-400/70",
                  ])}
                  initial={{ width: 0 }}
                  animate={{ width: isVisuallyActive ? "100%" : 0 }}
                  whileHover={{ width: "100%" }}
                  exit={{ width: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              </motion.li>
            );
          }
        )}
      </ul>
      {rightSide}
    </div>
  );
};

export default React.memo(LinksList);
